import {
  dateFromDateString,
  dateFromTimeString,
  DateString,
  makeZonedDateTime,
  monthDateString,
  Store,
  TimeOffEvent,
  TimeString,
  timeStringFrom24HourTimeString,
  timeToNearestIncrement,
  toDateString,
  toTimeString,
  UpdateTimeOffParams,
} from '@nextforce/api'
import { CheckBox, ListItem, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FlatList, View } from 'react-native'
import { BooleanRow } from 'shared/components/boolean_row'
import { HTMLText } from 'shared/components/html_text'
import { DateTimeChooser } from 'shared/components/list/date_time_chooser'
import { RoundedButton } from 'shared/components/rounded-button'
import { TextInputBox } from 'shared/components/text_input_box'
import { DEFAULT_STORE_PREFERENCES } from 'stores/constants'
import { Item } from '../../../item'

interface Props {
  store: Store | undefined
  timeOffEvent?: TimeOffEvent
  attestationCheck: boolean
  setAttestationCheck: React.Dispatch<React.SetStateAction<boolean>>
  onDelete(): void
  onTimeOffEventParamsChanged(timeOffEventData: UpdateTimeOffParams): void
  loading?: boolean
  lastScheduledDate?: string
}

const MAX_REASON_LENGTH = 128

export function EditRequestTimeOffView({
  store,
  timeOffEvent,
  lastScheduledDate,
  attestationCheck,
  setAttestationCheck,
  onDelete,
  onTimeOffEventParamsChanged,
  loading,
}: Props) {
  const { theme } = useTheme()
  const styles = useStyles()
  const preferences = store?.preferences ?? DEFAULT_STORE_PREFERENCES
  const firstUnscheduledDate = toDateString(
    moment(lastScheduledDate).add(1, 'day')
  )

  const currentTimeAtStore = timeToNearestIncrement(
    dateFromTimeString(toTimeString(moment()), store?.tz),
    preferences.minShiftDuration
  )

  const [startDate, setStartDate] = useState<DateString>(
    timeOffEvent?.startDate ? timeOffEvent.startDate : firstUnscheduledDate
  )

  const [endDate, setEndDate] = useState<DateString>(
    timeOffEvent?.endDate ? timeOffEvent.endDate : firstUnscheduledDate
  )

  const [startTime, setStartTime] = useState<TimeString>(
    timeOffEvent?.startTime ?? toTimeString(currentTimeAtStore)
  )

  const [endTime, setEndTime] = useState<TimeString>(
    timeOffEvent?.endTime ??
      toTimeString(
        moment(currentTimeAtStore).add(
          moment.duration(
            preferences.minShiftDuration ??
              DEFAULT_STORE_PREFERENCES.minShiftDuration,
            'minutes'
          )
        )
      )
  )

  const [allDay, setAllDay] = useState<boolean>(timeOffEvent?.allDay ?? true)
  const [reason, setReason] = useState<string>(timeOffEvent?.reason ?? '')

  const items = useMemo(() => {
    const allDayItem: Item = {
      type: 'allDay',
      key: 'allDay',
    }

    const reasonItem: Item = {
      type: 'reason',
      key: 'reason',
    }

    const startDateItem: Item = {
      type: 'startDate',
      key: 'startDate',
    }

    const startTimeItem: Item = {
      type: 'startTime',
      key: 'startTime',
    }

    const endDateItem: Item = {
      type: 'endDate',
      key: 'endDate',
    }

    const endTimeItem: Item = {
      type: 'endTime',
      key: 'endTime',
    }

    const explanationItem: Item = {
      type: 'explanation',
      key: 'explanation',
    }

    const attestationItem: Item = {
      type: 'attestation',
      key: 'attestation',
    }

    return [
      allDayItem,
      startDateItem,
      startTimeItem,
      endDateItem,
      endTimeItem,
      reasonItem,
      explanationItem,
      attestationItem,
    ]
  }, [])

  const onStartDateChange = useCallback((selectedDate: Date) => {
    setStartDate(toDateString(selectedDate))
  }, [])

  const onEndDateChange = useCallback((selectedDate: Date) => {
    setEndDate(toDateString(selectedDate))
  }, [])

  const onStartTimeChange = useCallback((selectedStartTime: Date) => {
    const startTimeString = toTimeString(selectedStartTime)
    setStartTime(startTimeString)
  }, [])

  const onEndTimeChange = useCallback((selectedEndTime: Date) => {
    const endTimeString = toTimeString(selectedEndTime)
    setEndTime(endTimeString)
  }, [])

  const onAllDayChanged = useCallback(() => {
    setAllDay(!allDay)
  }, [allDay])

  useEffect(() => {
    if (!timeOffEvent && allDay) {
      setStartTime(toTimeString(moment().startOf('day')))
      setEndTime(toTimeString(moment().endOf('day')))
      setEndDate(toDateString(startDate))
    }
  }, [allDay, startDate, timeOffEvent])

  useEffect(() => {
    if (startDate > endDate) {
      setEndDate(toDateString(startDate))
    }
    if (startDate === endDate && startTime >= endTime) {
      setEndDate(toDateString(moment(startDate).add(1, 'day')))
    }
  }, [endDate, endTime, startDate, startTime])

  useEffect(() => {
    onTimeOffEventParamsChanged({
      start_time: makeZonedDateTime(startDate, startTime, store?.tz),
      end_time: makeZonedDateTime(endDate, endTime, store?.tz),
      store_id: Number(store?.id),
      reason: reason,
    })
  }, [
    endDate,
    endTime,
    onTimeOffEventParamsChanged,
    reason,
    startDate,
    startTime,
    store?.id,
    store?.tz,
  ])

  const renderItem = useCallback(
    ({ item }: { item: Item }) => {
      if (!store) {
        return null
      }

      switch (item?.type) {
        case 'allDay':
          return (
            <View style={{ marginVertical: 5 }}>
              <BooleanRow
                title={t('allDay')}
                value={allDay}
                onToggle={onAllDayChanged}
              />
            </View>
          )
        case 'startDate':
          return (
            <DateTimeChooser
              title={t('startDate')}
              value={dateFromDateString(startDate)}
              mode="date"
              onChange={onStartDateChange}
              minDate={moment(firstUnscheduledDate).toDate()}
            />
          )
        case 'startTime':
          return allDay ? null : (
            <DateTimeChooser
              title={t('startTime')}
              value={dateFromTimeString(startTime)}
              mode="time"
              minuteInterval={preferences.shiftIncrements}
              onChange={onStartTimeChange}
            />
          )
        case 'endDate':
          return (
            <DateTimeChooser
              title={t('endDate')}
              value={dateFromDateString(endDate)}
              mode="date"
              onChange={onEndDateChange}
              minDate={moment(startDate).toDate()}
            />
          )
        case 'endTime':
          return allDay ? null : (
            <DateTimeChooser
              title={t('endTime')}
              value={dateFromTimeString(endTime)}
              mode="time"
              minuteInterval={preferences.shiftIncrements}
              onChange={onEndTimeChange}
            />
          )
        case 'reason':
          return (
            <ListItem containerStyle={styles.reasonContainer}>
              <ListItem.Title>{t('reasonForTimeOff')}</ListItem.Title>
              <View>
                <TextInputBox
                  onChangeText={setReason}
                  placeholder={t('addReasonForTimeOff')}
                  value={reason}
                  placeholderTextColor={
                    attestationCheck ? theme.colors.primary : undefined
                  }
                  maxLength={MAX_REASON_LENGTH}
                  style={styles.textInput}
                />
                {reason.length > 0 ? (
                  <Text style={styles.reasonLength}>
                    {`${reason.length}/${MAX_REASON_LENGTH} chars`}
                  </Text>
                ) : null}
              </View>
            </ListItem>
          )
        case 'explanation':
          return (
            <View style={styles.explanationContainer}>
              <HTMLText
                text={t('timeOffRequestExplanation')}
                style={styles.explanation}
              />
            </View>
          )
        case 'attestation':
          return (
            <CheckBox
              title={t('timeOffRequestAttestation')}
              checked={attestationCheck}
              containerStyle={styles.attestationContainer}
              textStyle={styles.attestationText}
              checkedColor={theme.colors.primary}
              onPress={() => setAttestationCheck(!attestationCheck)}
            />
          )
        default:
          return null
      }
    },
    [
      allDay,
      attestationCheck,
      endDate,
      endTime,
      firstUnscheduledDate,
      onAllDayChanged,
      onEndDateChange,
      onEndTimeChange,
      onStartDateChange,
      onStartTimeChange,
      preferences.shiftIncrements,
      reason,
      setAttestationCheck,
      startDate,
      startTime,
      store,
      styles.attestationContainer,
      styles.attestationText,
      styles.explanation,
      styles.explanationContainer,
      styles.reasonContainer,
      styles.reasonLength,
      styles.textInput,
      theme.colors.primary,
    ]
  )

  function dayMonthDateString(date: moment.MomentInput) {
    return moment(date).format('ddd MMM D')
  }

  const dateTimeRangeString = useCallback(() => {
    const timeOffDurationInDays =
      moment(endDate).diff(moment(startDate), 'days') + 1

    if (allDay) {
      if (startDate === endDate) {
        return `${t('days', {
          count: timeOffDurationInDays,
        })} (${monthDateString(startDate)})`
      }
      return `${t('days', {
        count: timeOffDurationInDays,
      })} (${monthDateString(startDate)} - ${monthDateString(endDate)})`
    }

    if (startDate === endDate) {
      return `${dayMonthDateString(startDate)} ${timeStringFrom24HourTimeString(
        startTime
      )} - ${timeStringFrom24HourTimeString(endTime)}`
    }
    return `${dayMonthDateString(startDate)} ${timeStringFrom24HourTimeString(
      startTime
    )} - ${dayMonthDateString(endDate)} ${timeStringFrom24HourTimeString(
      endTime
    )}`
  }, [allDay, endDate, endTime, startDate, startTime])

  return (
    <>
      <FlatList style={styles.container} data={items} renderItem={renderItem} />
      <View style={styles.footer}>
        <Text style={styles.footerText}>{dateTimeRangeString()}</Text>
        {timeOffEvent ? (
          <RoundedButton
            title={t('cancelRequest')}
            onPress={onDelete}
            danger
            loading={loading}
          />
        ) : (
          <></>
        )}
      </View>
    </>
  )
}

export const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: theme.colors?.white,
    flex: 1,
    marginTop: 10,
  },
  sectionHeaderText: {
    fontSize: 16,
    marginVertical: 10,
    marginHorizontal: 15,
  },
  footer: {
    padding: 20,
  },
  footerText: {
    color: theme.colors?.primary,
    fontFamily: font.bold,
    textAlign: 'center',
    marginBottom: 10,
  },
  reasonContainer: {
    flexDirection: 'column',
    flex: 1,
    alignItems: 'stretch',
    justifyContent: 'center',
    paddingTop: 10,
    paddingBottom: 20,
  },
  reasonLength: {
    position: 'absolute',
    bottom: 13,
    right: 10,
    color: theme.colors.grey2,
  },
  textInput: { paddingBottom: 20 },
  explanation: {
    color: theme.colors.grey2,
    textAlign: 'justify',
  },
  explanationContainer: { marginHorizontal: 30 },
  attestationContainer: {
    paddingLeft: 30,
    maxWidth: 300,
  },
  attestationText: {
    textAlign: 'justify',
  },
}))
